import React, { useEffect, useMemo, useState } from "react";
import "./strukturku.css";
import {
  STEPS, CATEGORIES, COMPANIES, PERSONS, COMPANY_ORDER, PERSON_ORDER, ddmmyyyy,
  registerGroupWithData, setActiveGroup,
} from "./data";
import { buildLayout, computeFlags, newEffCache, isUBOCandidate, LayoutOpts } from "./model";
import Canvas from "./Canvas";
import DetailPanel from "./DetailPanel";
import { shareApi, formatApiError } from "./api";
import { Building2, ShieldCheck, Eye, Lock } from "lucide-react";

const PUBLIC_GID = "__public__";
const noop = () => {};

export default function PublicView({ token }: { token: string }) {
  const [data, setData] = useState<{ name: string; group: any; overrides: any } | null>(null);
  const [err, setErr] = useState("");
  const [ready, setReady] = useState(false);
  const [step, setStep] = useState(0);
  const [selected, setSelected] = useState<{ kind: "person" | "company"; id: string } | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [hiddenOwners, setHiddenOwners] = useState<Set<string>>(new Set());
  const [pinnedId, setPinnedId] = useState<string | null>(null);
  const [catHighlight, setCatHighlight] = useState<string | null>(null);
  const [fitSignal, setFitSignal] = useState(0);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", "light");
    const robots = document.createElement("meta");
    robots.name = "robots";
    robots.content = "noindex, nofollow";
    document.head.appendChild(robots);
    shareApi.public(token)
      .then((res) => {
        registerGroupWithData(PUBLIC_GID, res.group);
        setActiveGroup(PUBLIC_GID);
        setData(res);
        setStep(Math.max(0, STEPS.length - 1));
        setReady(true);
      })
      .catch((e: any) => setErr(formatApiError(e?.detail) || "Tautan tidak tersedia."));
    return () => { document.head.removeChild(robots); };
  }, [token]);

  const opts: LayoutOpts = { collapsed, showEffective: true, drawSmall: false, showOfficers: true };
  const effStep = STEPS.length ? Math.min(step, STEPS.length - 1) : 0;
  const cache = useMemo(() => newEffCache(effStep), [effStep, ready]);
  const layout = useMemo(() => buildLayout(effStep, opts, cache),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [effStep, collapsed, cache, ready]);
  const flags = useMemo(() => computeFlags(effStep), [effStep, ready]);
  const uboSet = useMemo(() => {
    const s = new Set<string>();
    for (const pid of PERSON_ORDER) if (isUBOCandidate(pid, cache)) s.add(pid);
    return s;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cache, effStep, ready]);

  const companyCat = useMemo(() => {
    const o: Record<string, string> = {};
    if (data) for (const cid of (data.group.companyOrder || [])) o[cid] = data.group.companies[cid].cat;
    return { ...o, ...(data?.overrides?.companyCat || {}) };
  }, [data]);
  const catColors: Record<string, string> = data?.overrides?.catColors || {};
  const primaryKbli: Record<string, string> = data?.overrides?.primaryKbli || {};
  const companyOps: Record<string, string> = data?.overrides?.companyOps || {};
  const docExtra: Record<string, any> = data?.overrides?.docExtra || {};

  const usedCats = useMemo(() => {
    const s = new Set<string>();
    for (const cid of COMPANY_ORDER) { const k = companyCat[cid] ?? "none"; if (CATEGORIES[k]) s.add(k); }
    return s;
  }, [companyCat, ready]);

  const goStep = (i: number) => { setStep(Math.max(0, Math.min(STEPS.length - 1, i))); setFitSignal((f) => f + 1); };
  const active = STEPS[effStep];
  const uboNames = [...uboSet].map((pid) => PERSONS[pid]?.name ?? pid);

  if (err) {
    const expired = /kedaluwarsa/i.test(err);
    return (
      <div className="pub-msg" data-theme="light" data-testid="public-error">
        <Lock size={30} strokeWidth={1.8} />
        <div className="pub-msg-title serif">{expired ? "Tautan sudah kedaluwarsa" : "Tautan tidak tersedia"}</div>
        <div className="pub-msg-sub">{err}</div>
      </div>
    );
  }
  if (!ready) {
    return <div className="pub-msg" data-theme="light"><div className="pub-msg-title serif" data-testid="public-loading">Memuat bagan…</div></div>;
  }

  return (
    <div className="app-root public" data-testid="public-view">
      <header className="pub-header">
        <div className="pub-brand">
          <Building2 size={22} strokeWidth={2.2} className="pub-brand-mark" />
          <div className="pub-brand-txt">
            <span className="pub-word">STRUKTURKU</span>
            <span className="pub-sub">by Abadi Premium Consultant</span>
          </div>
        </div>
        <div className="pub-title">
          <span className="pub-grp serif" data-testid="public-group-name">{data?.name}</span>
          <span className="pub-badge"><Eye size={13} strokeWidth={2} /> Tampilan bersama · hanya-baca</span>
        </div>
        <div style={{ flex: 1 }} />
        {uboNames.length > 0 && (
          <div className="pub-ubo" data-testid="public-ubo">
            <span className="pub-ubo-lbl">Kandidat UBO (&gt;25%)</span>
            <span className="pub-ubo-names">{uboNames.join(", ")}</span>
          </div>
        )}
      </header>

      <div className="tl">
        <div className="tl-left">
          <div className="lbl">Struktur per tanggal</div>
          <div className="date serif tnum">{active ? ddmmyyyy(active.date) : "—"}</div>
        </div>
        <div className="tl-right">
          {STEPS.length === 0 ? (
            <div className="tl-caption"><span>Grup ini belum memiliki data akta.</span></div>
          ) : (
            <>
              <div className="tl-rail">
                {STEPS.map((s, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <div className={"tl-seg" + (i <= effStep ? " done" : "")} />}
                    <button className={"tl-node" + (i < effStep ? " done" : "") + (i === effStep ? " active done" : "")}
                      onClick={() => goStep(i)} data-testid={`step-${i}`} title={s.label}>{i + 1}</button>
                  </React.Fragment>
                ))}
              </div>
              <div className="tl-caption">
                <span>Langkah {effStep + 1} dari {STEPS.length}.</span>
                {active && <b>{active.label}.</b>}
                {active && <span>{active.detail}</span>}
              </div>
              <div className="tl-arrows">
                <button className="btn" onClick={() => goStep(effStep - 1)} data-testid="step-prev" disabled={effStep === 0}>←</button>
                <button className="btn" onClick={() => goStep(effStep + 1)} data-testid="step-next" disabled={effStep === STEPS.length - 1}>→</button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="body">
        <Canvas
          layout={layout}
          step={effStep}
          catColors={catColors}
          companyCat={companyCat}
          flags={flags}
          selectedId={selected?.id ?? null}
          onSelect={(kind, id) => setSelected({ kind, id })}
          onDeselect={() => setSelected(null)}
          search=""
          showOfficers={true}
          collapsed={collapsed}
          onToggleCollapse={(id) => setCollapsed((prev) => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; })}
          uboSet={uboSet}
          fitSignal={fitSignal}
          hiddenOwners={hiddenOwners}
          setHiddenOwners={setHiddenOwners}
          pinnedId={pinnedId}
          setPinnedId={(id) => { setPinnedId(id); if (id) setCatHighlight(null); }}
          onExportChain={noop}
          catHighlight={catHighlight}
          setCatHighlight={setCatHighlight}
        />

        <div className="legend" data-testid="public-legend">
          <h4>Warna kategori</h4>
          {Object.keys(CATEGORIES).filter((k) => usedCats.has(k)).map((k) => (
            <div className="legend-row" key={k}>
              <span className="legend-swatch" style={{ background: catColors[k] ?? CATEGORIES[k].color }} />
              <span>{CATEGORIES[k].name}</span>
            </div>
          ))}
        </div>

        {selected && (COMPANIES[selected.id] || PERSONS[selected.id]) && (
          <DetailPanel
            sel={selected}
            step={effStep}
            cache={cache}
            flags={flags}
            companyCat={companyCat}
            onChangeCat={noop}
            companyOps={companyOps}
            onChangeOps={noop}
            primaryKbli={primaryKbli}
            onChangePrimaryKbli={noop}
            docExtra={docExtra}
            onDocExtra={noop}
            onClose={() => setSelected(null)}
            onSelectEntity={(kind, id) => setSelected({ kind, id })}
            canEdit={false}
          />
        )}
      </div>

      <div className="pub-foot">
        <ShieldCheck size={14} strokeWidth={2} /> Bagan ini dibagikan untuk keperluan tinjauan. Data bersifat pendukung, bukan nasihat hukum.
      </div>
    </div>
  );
}
